import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Badge } from '../ui/badge';
import { ContentLabels } from '../../types/navigation';
import { isRTL, getTextDirection, getRTLClasses, getDirAttribute } from '../../utils/rtl';

interface PaymentPlanMilestone {
  percentage: number;
  milestone: string;
  dueDate: string;
  description?: string;
}

interface PaymentPlanHighlight {
  icon: string;
  title: string;
  description: string;
}

interface PaymentPlanOption {
  id: string;
  name: string;
  description: string;
  downPayment: {
    percentage: number;
    dueDate: string;
  };
  milestones: PaymentPlanMilestone[];
  highlights: PaymentPlanHighlight[];
}

interface PaymentPlanProps {
  title: string;
  description: string;
  plans: PaymentPlanOption[];
  language?: string;
  contentLabels?: ContentLabels;
}

interface ScrollArrowProps {
  direction: 'left' | 'right';
  onClick: () => void;
}

const IconMap = {
  "calendar": (
    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
    </svg>
  ),
  "payment": (
    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  ),
  "document": (
    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
    </svg>
  ),
  "money": (
    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  ),
  "shield": (
    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
    </svg>
  ),
  "star": (
    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
    </svg>
  )
};

const ScrollArrow = ({ direction, onClick }: ScrollArrowProps) => (
  <motion.button
    onClick={onClick}
    initial={{ opacity: 0 }}
    animate={{ opacity: 1 }}
    exit={{ opacity: 0 }}
    className={`absolute top-1/2 -translate-y-1/2 ${
      direction === 'left' ? 'left-1' : 'right-1'
    } z-20 bg-white/90 rounded-full p-2 shadow-md hover:bg-white transition-colors`}
  >
    <svg
      className="w-6 h-6 text-gray-600"
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d={direction === 'left' 
          ? "M15 19l-7-7 7-7"
          : "M9 5l7 7-7 7"
        }
      />
    </svg>
  </motion.button>
);

const PaymentPlan = ({
  title,
  description,
  plans,
  language,
  contentLabels
}: PaymentPlanProps) => {
  // Default fallback labels if contentLabels is not provided
  const labels = contentLabels || {
    paymentPlan: 'Payment Plan',
    downPayment: 'Down Payment',
    dueBy: 'Due by',
    noPaymentPlans: 'No payment plans available at this time.',
    // Other required fields with fallbacks
    availableUnitTypes: 'Available Unit Types',
    unitSize: 'Size:',
    startingFrom: 'Starting from',
    downloadBrochure: 'Download Brochure',
    contactExpert: 'Contact Expert',
    viewDetails: 'View Details',
    requestInfo: 'Request Information',
    bookTour: 'Book a Tour',
    whatsappAction: 'WhatsApp',
    callAction: 'Call Now',
    learnMore: 'Learn More',
    viewAll: 'View All',
    showMore: 'Show More',
    showLess: 'Show Less',
    frequentlyAskedQuestions: 'Frequently Asked Questions',
    faqDescription: 'Find answers to common questions about our property and investment opportunities',
    primeLocation: 'Prime Location',
    nearbyPlaces: 'Nearby Places',
    keyDistances: 'Key Distances',
    noNearbyPlaces: 'No nearby places available.',
    noKeyDistances: 'No key distances available.',
    projectGallery: 'Project Gallery',
    closeGallery: 'Close gallery',
    dldPermitNumber: 'DLD Permit Number',
    scanQrCode: 'Scan QR Code for more information',
    fullName: 'Full Name',
    firstName: 'First Name',
    lastName: 'Last Name',
    email: 'Email Address',
    phoneNumber: 'Phone Number',
    continue: 'Continue',
    back: 'Back',
    submit: 'Submit',
    submitting: 'Submitting...',
    thankYou: 'Thank You!',
    nameRequired: 'Name is required',
    emailRequired: 'Email is required',
    emailInvalid: 'Please enter a valid email',
    phoneRequired: 'Phone number is required'
  };

  // RTL detection and classes
  const rtlClasses = getRTLClasses(language || 'en');
  const dirAttribute = getDirAttribute(language || 'en');

  const [selectedPlan, setSelectedPlan] = useState(plans.length > 0 ? plans[0].id : '');
  const [showLeftArrow, setShowLeftArrow] = useState(false);
  const [showRightArrow, setShowRightArrow] = useState(false);
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const checkScroll = () => {
    const container = scrollContainerRef.current;
    if (container) {
      const { scrollLeft, scrollWidth, clientWidth } = container;
      setShowLeftArrow(scrollLeft > 0);
      setShowRightArrow(scrollLeft < scrollWidth - clientWidth - 10); // 10px threshold
    }
  };

  useEffect(() => {
    checkScroll();
    window.addEventListener('resize', checkScroll);
    return () => window.removeEventListener('resize', checkScroll);
  }, []);

  useEffect(() => {
    // Update selected plan when plans change
    if (plans.length > 0 && !plans.some(plan => plan.id === selectedPlan)) {
      setSelectedPlan(plans[0].id);
    }
  }, [plans, selectedPlan]);

  const handleScroll = (direction: 'left' | 'right') => {
    const container = scrollContainerRef.current;
    if (container) {
      const scrollAmount = 200;
      container.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth'
      });
    }
  };

  // If there are no plans, display a message instead of erroring
  if (plans.length === 0) {
    return (
      <section className={`py-16 md:py-24 bg-gray-50 ${rtlClasses}`} dir={dirAttribute}>
        <div className="container mx-auto px-4 text-center">
          <Badge variant="secondary" className="mb-4 text-lg px-4 py-1">
            {labels.paymentPlan}
          </Badge>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
            {title}
          </h2>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto">
            {description}
          </p>
          <div className="mt-12 p-8 bg-white rounded-xl shadow-sm">
            <p className="text-gray-500">{labels.noPaymentPlans}</p>
          </div>
        </div>
      </section>
    );
  }

  const activePlan = plans.find(plan => plan.id === selectedPlan) || plans[0];

  return (
    <section className="py-16 md:py-24 bg-gray-50">
      <div className="container mx-auto px-4">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <Badge 
            variant="secondary"
            className="mb-4 text-lg px-4 py-1"
          >
            {labels.paymentPlan}
          </Badge>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
            {title}
          </h2>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto">
            {description}
          </p>
        </motion.div>

        {/* Plan Selection */}
        <div className="flex justify-center mb-12 relative">
          <div 
            ref={scrollContainerRef}
            className="max-w-full overflow-x-auto scrollbar-hide px-4 -mx-4 relative"
            onScroll={checkScroll}
          >
            <div className="inline-flex rounded-lg bg-white shadow-sm p-2 min-w-fit">
              {plans.map((plan) => (
                <motion.button
                  key={plan.id}
                  onClick={() => setSelectedPlan(plan.id)}
                  className={`px-6 py-3 rounded-md text-sm font-medium whitespace-nowrap transition-colors relative ${
                    selectedPlan === plan.id 
                      ? 'text-white' 
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  {selectedPlan === plan.id && (
                    <motion.div
                      layoutId="planSelection"
                      className="absolute inset-0 bg-primary rounded-md"
                      initial={false}
                      transition={{ type: "spring", bounce: 0.2, duration: 0.6 }}
                    />
                  )}
                  <span className="relative z-10">{plan.name}</span>
                </motion.button>
              ))}
            </div>
          </div>
          
          <AnimatePresence>
            {showLeftArrow && (
              <ScrollArrow key="left-arrow" direction="left" onClick={() => handleScroll('left')} />
            )}
            {showRightArrow && (
              <ScrollArrow key="right-arrow" direction="right" onClick={() => handleScroll('right')} />
            )}
          </AnimatePresence>

          <div className="absolute left-0 top-0 bottom-0 w-4 bg-gradient-to-r from-gray-50 to-transparent pointer-events-none" />
          <div className="absolute right-0 top-0 bottom-0 w-4 bg-gradient-to-l from-gray-50 to-transparent pointer-events-none" />
        </div>

        {/* Plan Description */}
        <AnimatePresence mode="wait">
          {activePlan && (
            <motion.div
              key={`description-${selectedPlan}`}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.3 }}
              className="text-center mb-12"
            >
              <p className="text-gray-600 max-w-2xl mx-auto">
                {activePlan.description}
              </p>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Down Payment Highlight */}
        <AnimatePresence mode="wait">
          {activePlan && (
            <motion.div
              key={`downpayment-${selectedPlan}`}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 20 }}
              transition={{ duration: 0.4 }}
              className="bg-white rounded-2xl shadow-sm p-8 mb-12 text-center"
            >
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-primary/10 text-primary mb-6">
                {IconMap["payment"]}
              </div>
              <motion.h3 
                initial={{ scale: 0.9 }}
                animate={{ scale: 1 }}
                className="text-2xl font-bold text-gray-900 mb-2"
              >
                {activePlan.downPayment.percentage}% {labels.downPayment}
              </motion.h3>
              <p className="text-gray-600">
                {labels.dueBy} {activePlan.downPayment.dueDate}
              </p>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Payment Timeline */}
        <AnimatePresence mode="wait">
          {activePlan && (
            <motion.div
              key={`timeline-${selectedPlan}`}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.3 }}
              className="mb-16"
            >
              <div className="relative">
                {activePlan.milestones.map((milestone, index) => (
                  <motion.div
                    key={`${selectedPlan}-milestone-${index}-${milestone.milestone}`}
                    initial={{ opacity: 0, x: index % 2 === 0 ? -20 : 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.1, duration: 0.4 }}
                    className={`flex items-start gap-8 mb-8 ${
                      index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'
                    }`}
                  >
                    <div className="flex-1 bg-white rounded-xl p-6 shadow-sm">
                      <div className="flex items-center gap-4 mb-4">
                        <motion.span 
                          initial={{ scale: 0.8 }}
                          animate={{ scale: 1 }}
                          className="text-2xl font-bold text-primary"
                        >
                          {milestone.percentage}%
                        </motion.span>
                        <div className="h-px flex-1 bg-gray-200" />
                        <span className="text-gray-500">{milestone.dueDate}</span>
                      </div>
                      <h4 className="text-xl font-semibold text-gray-900 mb-2">
                        {milestone.milestone}
                      </h4>
                      {milestone.description && (
                        <p className="text-gray-600">{milestone.description}</p>
                      )}
                    </div>
                    <div className="hidden md:block flex-1" />
                  </motion.div>
                ))}
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Highlights */}
        <AnimatePresence mode="wait">
          {activePlan && (
            <motion.div
              key={`highlights-${selectedPlan}`}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.4 }}
              className="grid grid-cols-1 md:grid-cols-3 gap-8"
            >
              {activePlan.highlights.map((highlight, index) => (
                <motion.div
                  key={`${selectedPlan}-highlight-${index}-${highlight.title}`}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1, duration: 0.4 }}
                  className="bg-white rounded-xl p-6 shadow-sm"
                >
                  <motion.div 
                    initial={{ scale: 0.8, rotate: -10 }}
                    animate={{ scale: 1, rotate: 0 }}
                    transition={{ delay: index * 0.1 + 0.2, duration: 0.4 }}
                    className="flex-shrink-0 w-12 h-12 rounded-lg bg-primary/10 text-primary flex items-center justify-center mb-4"
                  >
                    {IconMap[highlight.icon as keyof typeof IconMap]}
                  </motion.div>
                  <h4 className="text-xl font-semibold text-gray-900 mb-2">
                    {highlight.title}
                  </h4>
                  <p className="text-gray-600">
                    {highlight.description}
                  </p>
                </motion.div>
              ))}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </section>
  );
};

export default PaymentPlan; 